<template>
  <div class="dashboard">
    <MainNavbar />
    <div class="dashboard-content">

      <ToastContainer :toasts="toasts" @remove="removeToast" />

      <!-- Confirmation Modal -->
      <transition name="modal-fade">
        <div v-if="showConfirmationModal" class="confirmation-modal">
          <div class="confirmation-content" @click.stop>
            <div class="confirmation-header">
              <i :class="confirmationModal.icon"></i>
              <h3>{{ confirmationModal.title }}</h3>
            </div>
            <div class="confirmation-body">
              <p>{{ confirmationModal.message }}</p>
              <div v-if="confirmationModal.bookingId" class="booking-info">
                <span>Booking #{{ confirmationModal.bookingId }}</span>
              </div>
            </div>
            <div class="confirmation-actions">
              <button @click="cancelAction" class="btn-cancel">
                <i class="fas fa-times"></i> Cancel
              </button>
              <button @click="confirmAction" class="btn-confirm">
                <i class="fas fa-check"></i> {{ confirmationModal.confirmText }}
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Profile / pitch load error -->
      <div v-if="profileLoadError" class="dashboard-error-state">
        <div class="error-content">
          <i class="fa-solid fa-triangle-exclamation error-icon"></i>
          <h3>Could not load pitch dashboard</h3>
          <p>{{ profileErrorMessage }}</p>
          <button type="button" class="retry-btn" @click="retryLoad">
            <i class="fa-solid fa-rotate"></i>
            Try again
          </button>
        </div>
      </div>

      <template v-else>
      <!-- Pitch profile header (aligned with SellerDashboard) -->
      <div class="profile-header">
        <div class="profile-info">
          <div class="profile-avatar pitch-avatar-wrap">
            <img
              v-if="pitchHeaderImage"
              class="profile-img"
              :src="pitchHeaderImage"
              alt="Pitch"
              decoding="async"
              @error="pitchImageFailed = true"
            />
            <div v-else class="pitch-avatar-fallback">
              <i class="fa-solid fa-futbol"></i>
            </div>
          </div>
          <div class="profile-details">
            <h2 class="seller-name">{{ pitch.pitch_name || 'Your pitch' }}</h2>
            <div class="profile-meta">
              <span class="seller-role">
                <i class="fa-solid fa-id-card"></i>
                ID: {{ pitch.pitch_id || '—' }}
              </span>
              <span class="seller-number">
                <i class="fa-solid fa-layer-group"></i>
                {{ formatPitchTier(pitch.pitch_tier) || '—' }} · {{ pitch.pitch_type || '—' }}
              </span>
            </div>
            <div class="approval-status" :class="ownershipStatusClass">
              <i :class="ownershipStatusIcon"></i>
              <span>{{ ownershipStatusLabel }}</span>
              <span v-if="pitch.is_verified" class="status-note verified-note">
                <i class="fa-solid fa-shield-check"></i> Verified
              </span>
            </div>
            <p v-if="lastBookingLabel" class="last-booking-hint">
              <i class="fa-solid fa-clock-rotate-left"></i>
              Last booking: {{ lastBookingLabel }}
            </p>
          </div>
        </div>
        <div class="profile-actions">
          <button type="button" class="dashboard-btn" title="Refresh" @click="refreshDashboard">
            <i class="fa-solid fa-rotate"></i>
          </button>
          <button type="button" class="edit-btn" @click="editPitchInfo" :disabled="loading">
            <i class="fa-solid fa-pen-to-square"></i>
            <span class="btn-text">Edit pitch</span>
          </button>
        </div>
      </div>

      <!-- Stats from PitchOwnership + live pending count -->
      <div class="stats-grid">
        <div
          v-for="item in pitchWidgetData"
          :key="item.key"
          class="stats-card"
        >
          <div class="stats-card-inner">
            <div class="stats-icon" :style="{ backgroundColor: item.color + '20' }">
              <i :class="item.icon" :style="{ color: item.color }"></i>
            </div>
            <div class="stats-content">
              <h3 class="stats-value">{{ item.value }}</h3>
              <p class="stats-label">{{ item.label }}</p>
              <p v-if="item.subtitle" class="stats-sublabel">{{ item.subtitle }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="pitch-dashboard-main">
        <!-- Pitch Information Card -->
        <div class="pitch-info-card">
          <div class="card-header">
            <div class="card-title">
              <i class="fas fa-futbol"></i>
              <h2>Pitch Information</h2>
            </div>
            <button class="edit-btn" @click="editPitchInfo" :disabled="loading">
              <i class="fas fa-edit"></i>
              <span>Edit Pitch</span>
            </button>
          </div>
          
          <div class="pitch-details">
            <div class="detail-row" v-for="detail in pitchDetails" :key="detail.label">
              <div class="detail-label">
                <i :class="detail.icon"></i>
                <span>{{ detail.label }}</span>
              </div>
              <div class="detail-value" :class="detail.class">
                <span v-if="detail.key === 'amenities' && pitch[detail.key]">
                  <div class="amenities-grid">
                    <div 
                      v-for="amenity in pitch[detail.key]" 
                      :key="amenity"
                      class="amenity-item"
                      :title="formatAmenity(amenity)"
                    >
                      <i :class="getAmenityIcon(amenity)"></i>
                      <span class="amenity-tooltip">{{ formatAmenity(amenity) }}</span>
                    </div>
                  </div>
                </span>
                <span v-else-if="detail.key === 'hourly_rate'">
                  Ksh {{ formatNumber(pitch[detail.key]) }}
                </span>
                <span v-else-if="detail.key === 'is_available'">
                  <label class="availability-toggle">
                    <input 
                      type="checkbox" 
                      v-model="pitch.is_available" 
                      @change="updateAvailability"
                      :disabled="loading"
                    >
                    <span class="slider">
                      <span class="slider-text">{{ pitch.is_available ? 'Available' : 'Unavailable' }}</span>
                    </span>
                  </label>
                </span>
                <span v-else-if="detail.key === 'status'">
                  <span class="status-badge" :class="pitch.status">
                    {{ ownershipStatusLabel }}
                  </span>
                </span>
                <span v-else-if="detail.key === 'pitch_tier'">
                  {{ formatPitchTier(pitch.pitch_tier) || 'Not set' }}
                </span>
                <span v-else>
                  {{ pitch[detail.key] || 'Not set' }}
                </span>
              </div>
            </div>
          </div>
          
          <!-- Pitch Image Preview -->
          <div v-if="pitch.images && pitch.images.length > 0" class="pitch-images">
            <div class="image-preview" @click="openImageGallery">
              <img
                :src="pitch.images[0]"
                alt="Pitch"
                loading="lazy"
                decoding="async"
                @error="handleImageError"
              />
              <div class="image-overlay">
                <i class="fas fa-images"></i>
                <span style="color: white;">{{ pitch.images.length }} images</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Bookings Section -->
        <div class="bookings-section">
          <div class="section-header">
            <div class="section-title">
              <i class="fas fa-calendar-alt"></i>
              <h2>Bookings Management</h2>
            </div>
            <div class="section-controls">
              <div class="date-filter">
                <input 
                  type="date" 
                  v-model="dateFilter"
                  @change="filterBookings"
                />
                <i class="fas fa-calendar"></i>
              </div>
            </div>
          </div>

          <!-- Filter Tabs -->
          <div class="filter-tabs">
            <button 
              v-for="tab in bookingTabs" 
              :key="tab.key"
              :class="['tab-btn', { active: activeTab === tab.key, 'has-count': getTabCount(tab.key) > 0 }]"
              @click="setActiveTab(tab.key)"
            >
              <span class="tab-label">{{ tab.label }}</span>
              <span class="tab-count">{{ getTabCount(tab.key) }}</span>
            </button>
          </div>

          <!-- Bookings List -->
          <div class="bookings-list">
            <!-- Empty State -->
            <div v-if="filteredBookings.length === 0" class="empty-state">
              <div class="empty-icon">
                <i :class="emptyState.icon"></i>
              </div>
              <h3>{{ emptyState.title }}</h3>
              <p>{{ emptyState.message }}</p>
              <button v-if="activeTab === 'all'" @click="fetchBookings" class="btn-refresh">
                <i class="fas fa-sync-alt"></i> Refresh
              </button>
            </div>

            <!-- Booking Cards -->
            <transition-group name="booking-list" tag="div" class="booking-list-container">
              <div 
                v-for="booking in paginatedBookings" 
                :key="booking.booking_number"
                class="booking-card"
                :class="[booking.status, { urgent: booking.is_urgent }]"
                @click="viewBookingDetails(booking)"
              >
                <div class="booking-header">
                  <div class="booking-id">
                    <i class="fas fa-hashtag"></i>
                    <span>#{{ booking.booking_number }}</span>
                    <span v-if="booking.is_urgent" class="urgent-pill">
                      <i class="fas fa-bolt"></i>
                      Urgent
                    </span>
                  </div>
                  <div class="booking-meta">
                    <span class="booking-date">
                      <i class="fas fa-calendar-day"></i>
                      {{ formatDate(booking.date) }}
                    </span>
                    <div class="booking-status" :class="booking.status">
                      <i :class="getStatusIcon(booking.status)"></i>
                      <span>{{ booking.status }}</span>
                    </div>
                  </div>
                </div>
                
                <div class="booking-body">
                  <div class="booking-customer">
                    <div class="customer-avatar">
                      <i class="fas fa-user-circle"></i>
                    </div>
                    <div class="customer-info">
                      <h4>{{ booking.customer_name }}</h4>
                      <div class="customer-contact">
                        <span v-if="booking.customer_phone">
                          <i class="fas fa-phone"></i>
                          {{ booking.customer_phone }}
                        </span>
                        <span>
                          <i class="fas fa-envelope"></i>
                          {{ booking.customer_email }}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="booking-details">
                    <div class="time-details">
                      <div class="time-slot">
                        <i class="fas fa-clock"></i>
                        <span>{{ booking.start_time }} - {{ booking.end_time }}</span>
                      </div>
                      <div class="duration">
                        <i class="fas fa-hourglass-half"></i>
                        <span>{{ calculateDuration(booking.start_time, booking.end_time) }} hours</span>
                      </div>
                    </div>
                    
                    <div class="booking-amount">
                      <div class="amount-label">Total Amount</div>
                      <div class="amount-value">Ksh {{ formatNumber(booking.total_cost) }}</div>
                    </div>
                  </div>
                  
                  <div v-if="booking.notes" class="booking-notes">
                    <i class="fas fa-sticky-note"></i>
                    <p>{{ truncateText(booking.notes, 100) }}</p>
                  </div>
                </div>
                
                <div class="booking-actions" @click.stop>
                  <button 
                    v-if="booking.status === 'pending'"
                    @click="showConfirmation('confirm', booking.booking_number)"
                    class="btn-action btn-success"
                    :disabled="loading"
                  >
                    <i class="fas fa-check-circle"></i>
                    <span>Confirm</span>
                  </button>
                  <button 
                    v-if="booking.status === 'pending'"
                    @click="showConfirmation('cancel', booking.booking_number)"
                    class="btn-action btn-danger"
                    :disabled="loading"
                  >
                    <i class="fas fa-times-circle"></i>
                    <span>Reject</span>
                  </button>
                  <button 
                    v-if="booking.status === 'confirmed'"
                    @click="showConfirmation('complete', booking.booking_number)"
                    class="btn-action btn-primary"
                    :disabled="loading"
                  >
                    <i class="fas fa-flag-checkered"></i>
                    <span>Complete</span>
                  </button>
                  <button 
                    class="btn-action btn-secondary"
                    @click="viewBookingDetails(booking)"
                    :disabled="loading"
                  >
                    <i class="fas fa-eye"></i>
                    <span>Details</span>
                  </button>
                </div>
              </div>
            </transition-group>
          </div>

          <!-- Pagination -->
          <div v-if="filteredBookings.length > itemsPerPage" class="pagination">
            <button 
              @click="previousPage" 
              :disabled="currentPage === 1"
              class="pagination-btn"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
            
            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="goToPage(page)"
                class="page-btn"
                :class="{ active: page === currentPage }"
              >
                {{ page }}
              </button>
            </div>
            
            <button 
              @click="nextPage" 
              :disabled="currentPage === totalPages"
              class="pagination-btn"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
      </template>

    <!-- Booking Details Modal -->
    <transition name="modal-slide">
      <div v-if="selectedBooking" class="modal-overlay" @click.self="selectedBooking = null">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <div class="modal-title">
              <i class="fas fa-file-invoice"></i>
              <h2>Booking #{{ selectedBooking.booking_number }}</h2>
            </div>
            <button @click="selectedBooking = null" class="close-btn">
              <i class="fas fa-times"></i>
            </button>
          </div>
          
          <div class="modal-body">
            <div v-if="selectedBooking.is_urgent" class="urgent-alert">
              <i class="fas fa-bolt"></i>
              <div>
                <strong>Urgent neutral-ground match booking</strong>
                <p>{{ selectedBooking.urgency_reason || "Confirm quickly so teams can prepare and avoid match disruption." }}</p>
              </div>
            </div>
            <!-- Status Indicator -->
            <div class="status-indicator" :class="selectedBooking.status">
              <div class="status-dot"></div>
              <span>{{ selectedBooking.status.toUpperCase() }}</span>
            </div>

            <!-- Customer Card -->
            <div class="customer-card">
              <div class="customer-header">
                <div class="customer-avatar-large">
                  <i class="fas fa-user-circle"></i>
                </div>
                <div class="customer-info">
                  <h3>{{ selectedBooking.customer_name }}</h3>
                  <div class="customer-contact">
                    <a :href="`mailto:${selectedBooking.customer_email}`">
                      <i class="fas fa-envelope"></i>
                      {{ selectedBooking.customer_email }}
                    </a>
                    <a v-if="selectedBooking.customer_phone" :href="`tel:${selectedBooking.customer_phone}`">
                      <i class="fas fa-phone"></i>
                      {{ selectedBooking.customer_phone }}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <!-- Booking Timeline -->
            <div class="booking-timeline">
              <div class="timeline-item">
                <div class="timeline-icon">
                  <i class="fas fa-calendar-check"></i>
                </div>
                <div class="timeline-content">
                  <strong>Booking Date</strong>
                  <p>{{ formatDate(selectedBooking.date) }}</p>
                </div>
              </div>
              <div class="timeline-item">
                <div class="timeline-icon">
                  <i class="fas fa-clock"></i>
                </div>
                <div class="timeline-content">
                  <strong>Time Slot</strong>
                  <p>{{ selectedBooking.start_time }} - {{ selectedBooking.end_time }}</p>
                  <small>{{ calculateDuration(selectedBooking.start_time, selectedBooking.end_time) }} hours</small>
                </div>
              </div>
              <div class="timeline-item">
                <div class="timeline-icon">
                  <i class="fas fa-money-bill-wave"></i>
                </div>
                <div class="timeline-content">
                  <strong>Total Amount</strong>
                  <p class="price">Ksh {{ formatNumber(selectedBooking.total_cost) }}</p>
                </div>
              </div>
            </div>

            <!-- Notes Section -->
            <div v-if="selectedBooking.notes" class="notes-section">
              <h4><i class="fas fa-sticky-note"></i> Customer Notes</h4>
              <div class="notes-content">
                {{ selectedBooking.notes }}
              </div>
            </div>

            <!-- Pitch Details -->
            <div class="pitch-details-section">
              <h4><i class="fas fa-futbol"></i> Pitch Details</h4>
              <div class="pitch-info-grid">
                <div class="info-item">
                  <span class="label">Name</span>
                  <span class="value">{{ pitch.pitch_name }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Location</span>
                  <span class="value">{{ pitch.address }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Rate</span>
                  <span class="value">Ksh {{ formatNumber(pitch.hourly_rate) }}/hour</span>
                </div>
              </div>
            </div>

            <!-- Timeline Events -->
            <div class="timeline-events">
              <h4><i class="fas fa-history"></i> Activity Timeline</h4>
              <div class="events-list">
                <div class="event-item">
                  <div class="event-dot"></div>
                  <div class="event-content">
                    <strong>Booking Created</strong>
                    <p>{{ formatDateTime(selectedBooking.created_at) }}</p>
                  </div>
                </div>
                <div v-if="selectedBooking.updated_at !== selectedBooking.created_at" class="event-item">
                  <div class="event-dot"></div>
                  <div class="event-content">
                    <strong>Last Updated</strong>
                    <p>{{ formatDateTime(selectedBooking.updated_at) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button @click="selectedBooking = null" class="btn-secondary">
              <i class="fas fa-times"></i> Close
            </button>
            <button 
              v-if="selectedBooking.status === 'pending'"
              @click="showConfirmation('confirm', selectedBooking.booking_number)"
              class="btn-success"
            >
              <i class="fas fa-check"></i> Confirm Booking
            </button>
          </div>
        </div>
      </div>
    </transition>

    </div>

    <div class="edit-pitch-footer-host">
      <ThirdpartyFooter />
    </div>
  </div>
</template>

<script>
/**
 * @fileoverview Pitch owner dashboard: pitch profile + booking list/actions.
 * @module routes/facility/FacilityDashboard
 *
 * - Pitch: `GET /api/pitch-owners/my-pitch-ownership` (null body if no pitch registered).
 * - Bookings: `GET /api/bookings/my-bookings`; status updates via `PUT /api/bookings/booking/:id/status` and cancel route.
 * - Availability toggle: `PUT /api/bookings/availability` with `{ is_available }`.
 */
import MainNavbar from "../../components/main/headernfooter/header.vue";
import ThirdpartyFooter from "@/routes/thirdparty/Footer.vue";
import ToastContainer from "@/components/ui/ToastContainer.vue";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import toastMixin from "@/mixins/toastMixin";
import { buildApiUrl } from "@/config/api";

export default {
  name: "FacilityOwnerDashboard",
  loadingMessage: "Fetching your pitch and bookings…",
  mixins: [toastMixin, globalLoadingMixin],
  components: { MainNavbar, ThirdpartyFooter, ToastContainer },
  data() {
    return {
      profileLoadError: false,
      profileErrorMessage: "We could not load your pitch. Register a pitch or try again.",
      pitchImageFailed: false,
      pitchAnalytics: {
        total_bookings_count: 0,
        total_revenue_kes: 0,
        listing_view_count: 0,
        last_booking_at: null,
      },
      pitch: {
        pitch_id: null,
        pitch_name: "",
        pitch_type: "",
        pitch_tier: "",
        hourly_rate: 0,
        capacity: 0,
        address: "",
        city: "",
        country: "",
        status: "",
        is_available: true,
        is_verified: false,
        amenities: [],
        images: [],
        pitch_description: "",
        zone: "",
        pin_location_url: "",
      },
      bookings: [],
      stats: {
        totalBookings: 0,
        pendingBookings: 0,
        confirmedBookings: 0,
        completedBookings: 0,
        totalRevenue: 0,
        thisWeek: 0,
        thisMonth: 0,
        confirmationRate: 0
      },
      activeTab: 'all',
      selectedBooking: null,
      loading: true,
      dateFilter: '',
      currentPage: 1,
      itemsPerPage: 6,
      showConfirmationModal: false,
      confirmationModal: {
        action: '',
        bookingId: null,
        title: '',
        message: '',
        icon: '',
        confirmText: ''
      },
      bookingTabs: [
        { key: "all", label: "All Bookings" },
        { key: "pending", label: "Pending" },
        { key: "confirmed", label: "Confirmed" },
        { key: "completed", label: "Completed" },
        { key: "cancelled", label: "Cancelled" },
      ],
      pitchDetails: [
        { label: "Pitch Name", key: "pitch_name", icon: "fas fa-signature" },
        { label: "Sport Type", key: "pitch_type", icon: "fas fa-running" },
        { label: "Tier Level", key: "pitch_tier", icon: "fas fa-layer-group" },
        { label: "Hourly Rate", key: "hourly_rate", icon: "fas fa-tag" },
        { label: "Capacity", key: "capacity", icon: "fas fa-users" },
        { label: "Street / address", key: "address", icon: "fas fa-map-marker-alt" },
        { label: "County / city", key: "city", icon: "fas fa-city" },
        { label: "Country", key: "country", icon: "fas fa-globe" },
        { label: "Amenities", key: "amenities", icon: "fas fa-list-check" },
        { label: "Ownership", key: "status", icon: "fas fa-info-circle" },
        { label: "Availability", key: "is_available", icon: "fas fa-toggle-on" },
      ],
    };
  },
  computed: {
    isBasketballPitch() {
      return String(this.pitch.pitch_type || "").trim().toLowerCase().includes("basketball");
    },
    bookingApiBase() {
      return this.isBasketballPitch ? "/api/bookings/basketball" : "/api/bookings";
    },
    pitchHeaderImage() {
      if (this.pitchImageFailed) return "";
      const img = this.pitch.images && this.pitch.images[0];
      return img || "";
    },
    ownershipStatusClass() {
      const s = String(this.pitch.status || "").toLowerCase();
      if (s === "active") return "status-approved";
      if (s === "pending") return "status-pending";
      if (s === "suspended" || s === "terminated") return "status-rejected";
      return "status-unknown";
    },
    ownershipStatusIcon() {
      const s = String(this.pitch.status || "").toLowerCase();
      if (s === "active") return "fa-solid fa-circle-check";
      if (s === "pending") return "fa-solid fa-clock";
      if (s === "suspended" || s === "terminated") return "fa-solid fa-circle-xmark";
      return "fa-solid fa-circle-question";
    },
    ownershipStatusLabel() {
      const s = String(this.pitch.status || "").toLowerCase();
      if (s === "active") return "Active";
      if (s === "pending") return "Pending approval";
      if (s === "suspended") return "Suspended";
      if (s === "terminated") return "Terminated";
      return this.pitch.status || "Unknown";
    },
    lastBookingLabel() {
      const raw = this.pitchAnalytics.last_booking_at;
      if (!raw) return "";
      try {
        return new Date(raw).toLocaleString("en-KE", {
          dateStyle: "medium",
          timeStyle: "short",
        });
      } catch {
        return "";
      }
    },
    pitchWidgetData() {
      const pending = this.stats.pendingBookings;
      const apiBookings = this.pitchAnalytics.total_bookings_count ?? 0;
      const liveTotal = this.bookings.length;
      const totalDisplay = Math.max(apiBookings, liveTotal);
      const revenueApi = Number(this.pitchAnalytics.total_revenue_kes) || 0;
      const revenueLive = this.stats.totalRevenue;
      const revenueDisplay = Math.max(revenueApi, revenueLive);
      return [
        {
          key: "bookings",
          label: "Total bookings",
          subtitle: "All-time requests (synced with profile)",
          value: totalDisplay,
          icon: "fa-solid fa-calendar-check",
          color: "#3b82f6",
        },
        {
          key: "views",
          label: "Pitch views",
          subtitle: "Listing impressions",
          value: (this.pitchAnalytics.listing_view_count || 0).toLocaleString("en-KE"),
          icon: "fa-solid fa-eye",
          color: "#f59e0b",
        },
        {
          key: "revenue",
          label: "Realized revenue",
          subtitle: "KES from completed (paid) sessions",
          value: `KES ${this.formatNumber(revenueDisplay)}`,
          icon: "fa-solid fa-coins",
          color: "#10b981",
        },
        {
          key: "pending",
          label: "Pending now",
          subtitle: "Awaiting your action",
          value: pending,
          icon: "fa-solid fa-hourglass-half",
          color: "#8b5cf6",
        },
      ];
    },
    filteredBookings() {
      let filtered = this.bookings;
      
      // Filter by status
      if (this.activeTab !== 'all') {
        filtered = filtered.filter(booking => booking.status === this.activeTab);
      }
      
      // Filter by date
      if (this.dateFilter) {
        filtered = filtered.filter(booking => 
          new Date(booking.date).toISOString().split('T')[0] === this.dateFilter
        );
      }
      
      return [...filtered].sort((a, b) => {
        const aUrgent = a?.is_urgent ? 1 : 0;
        const bUrgent = b?.is_urgent ? 1 : 0;
        if (aUrgent !== bUrgent) return bUrgent - aUrgent;
        const aTime = new Date(a?.created_at || 0).getTime();
        const bTime = new Date(b?.created_at || 0).getTime();
        return bTime - aTime;
      });
    },
    paginatedBookings() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredBookings.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredBookings.length / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    emptyState() {
      if (this.dateFilter) {
        return {
          icon: 'fas fa-calendar-times',
          title: 'No results found',
          message: 'Try adjusting your date filter'
        };
      }
      
      const tab = this.bookingTabs.find(t => t.key === this.activeTab);
      return {
        icon: 'fas fa-calendar-times',
        title: `No ${tab?.label || 'bookings'} found`,
        message: 'When you receive bookings, they will appear here'
      };
    }
  },
  mounted() {
    this.loadDashboard();
  },
  methods: {
    normalizeBookingRow(b) {
      if (!b || typeof b !== "object") return b;
      return {
        ...b,
        status: String(b.status || "").toLowerCase(),
        total_cost: Number(b.total_cost) || 0,
        booking_number: b.booking_number,
        is_urgent: Boolean(b.is_urgent),
        urgency_level: b.urgency_level || "normal",
        urgency_reason: b.urgency_reason || null,
      };
    },

    /**
     * Parse FastAPI / HTTP JSON error bodies into a single message.
     */
    async parseApiError(response) {
      const text = await response.text();
      let body = null;
      try {
        body = text ? JSON.parse(text) : null;
      } catch {
        return text || response.statusText || `Request failed (${response.status})`;
      }
      if (response.status === 401) {
        return (
          (body && body.detail && typeof body.detail === "string" && body.detail) ||
          "Your session expired. Please sign in again."
        );
      }
      const detail = body && body.detail;
      if (detail == null || detail === "") {
        return (
          (body && body.message) ||
          text ||
          response.statusText ||
          `Request failed (${response.status})`
        );
      }
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) {
        return detail
          .map((d) => (d && (d.msg || d.message)) || JSON.stringify(d))
          .filter(Boolean)
          .join(" ");
      }
      if (typeof detail === "object") {
        if (detail.message) {
          const extra = detail.detail || detail.suggestion || "";
          return extra ? `${detail.message} ${extra}` : detail.message;
        }
        try {
          return JSON.stringify(detail);
        } catch {
          return "Something went wrong.";
        }
      }
      return String(detail);
    },
    
    showConfirmation(action, bookingId) {
      const messages = {
        confirm: {
          title: 'Confirm Booking',
          message:
            'Are you sure you want to confirm this booking?\n\n' +
            'An email will be sent to the client to confirm the booking. Please ensure that payment is completed beforehand, as the confirmation email will serve as the gate pass.',
          icon: 'fas fa-check-circle',
          confirmText: 'Yes, Confirm'
        },
        cancel: {
          title: 'Reject Booking',
          message: 'Are you sure you want to reject this booking? This action cannot be undone.',
          icon: 'fas fa-times-circle',
          confirmText: 'Yes, Reject'
        },
        complete: {
          title: 'Mark as Complete',
          message: 'Mark this booking as completed?',
          icon: 'fas fa-flag-checkered',
          confirmText: 'Yes, Complete'
        }
      };
      
      this.confirmationModal = {
        action,
        bookingId,
        ...messages[action]
      };
      this.showConfirmationModal = true;
    },
    
    async confirmAction() {
      const id = this.confirmationModal.bookingId;
      const action = this.confirmationModal.action;
      this.showConfirmationModal = false;
      if (action === "cancel") {
        await this.cancelBooking(id);
      } else {
        await this.updateBookingStatus(
          id,
          action === "confirm" ? "confirmed" : "completed"
        );
      }
    },
    
    cancelAction() {
      this.showConfirmationModal = false;
    },

    async loadDashboard() {
      this.loading = true;
      this.profileLoadError = false;
      const token = localStorage.getItem("access_token");
      if (!token) {
        this.loading = false;
        this.profileLoadError = true;
        this.profileErrorMessage = "Please sign in to view your pitch dashboard.";
        return;
      }
      try {
        const okPitch = await this.fetchPitchData({ silent: true });
        if (!okPitch) {
          this.loading = false;
          return;
        }
        await this.fetchBookings({ silent: true });
      } finally {
        this.loading = false;
      }
    },

    retryLoad() {
      this.pitchImageFailed = false;
      this.loadDashboard();
    },

    normalizePitchImageUrls(images) {
      if (images == null) return [];
      if (Array.isArray(images)) return images.filter(Boolean);
      if (typeof images === "string") {
        const s = images.trim();
        if (!s) return [];
        try {
          const parsed = JSON.parse(s);
          return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
        } catch {
          return [];
        }
      }
      return [];
    },

    async refreshDashboard() {
      await this.loadDashboard();
      this.showToast("Updated", "Dashboard refreshed", "success");
    },

    normalizePitchFromApi(data) {
      if (!data || typeof data !== "object") return;
      this.pitchAnalytics = {
        total_bookings_count: Number(data.total_bookings_count) || 0,
        total_revenue_kes: Number(data.total_revenue_kes) || 0,
        listing_view_count: Number(data.listing_view_count) || 0,
        last_booking_at: data.last_booking_at || null,
      };
      this.pitch = {
        ...this.pitch,
        pitch_id: data.id ?? data.pitch_id ?? null,
        pitch_name: data.pitch_name || "",
        pitch_type: data.pitch_type || "",
        pitch_tier: this.capitalizeTierWord(data.pitch_tier || ""),
        hourly_rate: data.hourly_rate ?? 0,
        capacity: data.capacity ?? data.pitch_capacity ?? 0,
        address: data.address || "",
        city: data.city || "",
        country: data.country || "",
        status: (data.status || "").toLowerCase() || "",
        is_available: !!data.is_available,
        is_verified: !!data.is_verified,
        amenities: Array.isArray(data.amenities) ? data.amenities : [],
        images: this.normalizePitchImageUrls(data.images),
        pitch_description: data.pitch_description || "",
        zone: data.zone || "",
        pin_location_url: data.pin_location_url || "",
      };
    },

    /**
     * @returns {Promise<boolean>} true if pitch payload applied
     */
    async fetchPitchData({ silent = false } = {}) {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          this.profileLoadError = true;
          return false;
        }
        const response = await fetch(buildApiUrl("/api/facilities/my-facility"), {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 404) {
          this.profileLoadError = true;
          this.profileErrorMessage =
            "No pitch found for this account. Complete pitch registration first.";
          if (!silent) {
            this.showToast("No pitch", this.profileErrorMessage, "warning");
          }
          return false;
        }

        const text = await response.text();
        if (!response.ok) {
          this.profileLoadError = true;
          this.profileErrorMessage = "Unable to load pitch details from the server.";
          if (!silent) this.showToast("Error", this.profileErrorMessage, "error");
          return false;
        }

        if (!text || text === "null") {
          this.profileLoadError = true;
          this.profileErrorMessage =
            "No pitch registered yet. Register your pitch to use this dashboard.";
          return false;
        }

        let data;
        try {
          data = JSON.parse(text);
        } catch {
          this.profileLoadError = true;
          this.profileErrorMessage = "Invalid response from server.";
          return false;
        }

        if (!data) {
          this.profileLoadError = true;
          this.profileErrorMessage =
            "No pitch registered yet. Register your pitch to use this dashboard.";
          return false;
        }

        this.profileLoadError = false;
        this.normalizePitchFromApi(data);
        this.pitchImageFailed = false;
        return true;
      } catch (error) {
        console.error(error);
        this.profileLoadError = true;
        this.profileErrorMessage = "Network error while loading pitch.";
        if (!silent) this.showToast("Error", this.profileErrorMessage, "error");
        return false;
      }
    },

    async fetchBookings({ silent = false } = {}) {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          this.bookings = [];
          this.calculateStats();
          return;
        }
        const response = await fetch(buildApiUrl(`${this.bookingApiBase}/my-bookings`), {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const list = await response.json();
          const raw = Array.isArray(list) ? list : [];
          this.bookings = raw.map((b) => this.normalizeBookingRow(b));
          this.calculateStats();
        } else {
          const msg = await this.parseApiError(response);
          this.bookings = [];
          this.calculateStats();
          if (!silent) {
            this.showToast(
              "Could not load bookings",
              msg || "Please refresh or try again later.",
              "error"
            );
          }
        }
      } catch (error) {
        console.error(error);
        this.bookings = [];
        this.calculateStats();
        if (!silent) {
          this.showToast(
            "Error",
            error.message || "Network error while loading bookings.",
            "error"
          );
        }
      }
    },

    calculateStats() {
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      this.stats.totalBookings = this.bookings.length;
      this.stats.pendingBookings = this.bookings.filter((b) => b.status === "pending").length;
      this.stats.confirmedBookings = this.bookings.filter((b) => b.status === "confirmed").length;
      this.stats.completedBookings = this.bookings.filter((b) => b.status === "completed").length;
      // Revenue = completed sessions only (treated as paid / realized)
      this.stats.totalRevenue = this.bookings
        .filter((b) => b.status === "completed")
        .reduce((sum, booking) => sum + (Number(booking.total_cost) || 0), 0);

      this.stats.thisWeek = this.bookings.filter(
        (b) => new Date(b.date) >= startOfWeek
      ).length;

      this.stats.thisMonth = this.bookings.filter(
        (b) => new Date(b.date) >= thisMonthStart
      ).length;

      this.stats.confirmationRate =
        this.bookings.length > 0
          ? Math.round((this.stats.confirmedBookings / this.bookings.length) * 100)
          : 0;
    },

    async updateBookingStatus(bookingId, newStatus) {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          this.showToast("Error", "Please sign in again.", "error");
          return;
        }
        const response = await fetch(
          buildApiUrl(`${this.bookingApiBase}/booking/${bookingId}/status`),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ status: newStatus }),
          }
        );

        if (response.ok) {
          const bookingIndex = this.bookings.findIndex(
            (b) => b.booking_number === bookingId
          );
          if (bookingIndex !== -1) {
            this.bookings[bookingIndex].status = newStatus;
            this.calculateStats();
          }
          if (
            this.selectedBooking &&
            this.selectedBooking.booking_number === bookingId
          ) {
            this.selectedBooking = {
              ...this.selectedBooking,
              status: newStatus,
            };
          }
          await this.fetchPitchData({ silent: true });
          await this.fetchBookings({ silent: true });
          const message =
            newStatus === "confirmed"
              ? `Booking #${bookingId} confirmed. Email sent to the customer.`
              : newStatus === "completed"
                ? `Booking #${bookingId} marked completed. Realized revenue updated.`
                : `Booking #${bookingId} is now ${newStatus}.`;
          this.showToast("Success", message, "success");
        } else {
          const msg = await this.parseApiError(response);
          this.showToast("Could not update booking", msg, "error");
        }
      } catch (error) {
        console.error(error);
        this.showToast(
          "Error",
          error.message || "Failed to update booking status.",
          "error"
        );
      }
    },

    async cancelBooking(bookingId) {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          this.showToast("Error", "Please sign in again.", "error");
          return;
        }
        const response = await fetch(
          buildApiUrl(`${this.bookingApiBase}/booking/${bookingId}/cancel`),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const bookingIndex = this.bookings.findIndex(
            (b) => b.booking_number === bookingId
          );
          if (bookingIndex !== -1) {
            this.bookings[bookingIndex].status = "cancelled";
            this.calculateStats();
          }
          if (
            this.selectedBooking &&
            this.selectedBooking.booking_number === bookingId
          ) {
            this.selectedBooking = {
              ...this.selectedBooking,
              status: "cancelled",
            };
          }
          await this.fetchPitchData({ silent: true });
          await this.fetchBookings({ silent: true });
          this.showToast(
            "Success",
            `Booking #${bookingId} rejected. The customer has been notified.`,
            "success"
          );
        } else {
          const msg = await this.parseApiError(response);
          this.showToast("Could not update booking", msg, "error");
        }
      } catch (error) {
        console.error(error);
        this.showToast(
          "Error",
          error.message || "Failed to cancel booking.",
          "error"
        );
      }
    },

    async updateAvailability() {
      try {
        const token = localStorage.getItem("access_token");
        const response = await fetch(
          buildApiUrl("/api/bookings/availability"),
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ is_available: this.pitch.is_available })
          }
        );

        if (response.ok) {
          const message = this.pitch.is_available 
            ? 'Pitch is now available for bookings!'
            : 'Pitch marked as unavailable. No new bookings will be accepted.';
          this.showToast('Success', message, 'success');
        }
      } catch (error) {
        this.showToast('Error', 'Failed to update availability', 'error');
        this.pitch.is_available = !this.pitch.is_available;
      }
    },

    filterBookings() {
      this.currentPage = 1;
    },
    
    setActiveTab(tab) {
      this.activeTab = tab;
      this.currentPage = 1;
    },
    
    getTabCount(tabKey) {
      if (tabKey === 'all') return this.bookings.length;
      return this.bookings.filter(booking => booking.status === tabKey).length;
    },
    
    getStatusIcon(status) {
      const icons = {
        pending: 'fas fa-clock',
        confirmed: 'fas fa-check-circle',
        completed: 'fas fa-flag-checkered',
        cancelled: 'fas fa-times-circle'
      };
      return icons[status] || 'fas fa-circle';
    },
    
    formatNumber(num) {
      return new Intl.NumberFormat('en-KE').format(num);
    },
    
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    },
    
    formatDateTime(dateTimeString) {
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    truncateText(text, length) {
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },
    
    previousPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    
    goToPage(page) {
      this.currentPage = page;
    },
    
    // Existing methods
    getAmenityIcon(amenity) {
      const icons = {
        lighting: 'fas fa-lightbulb',
        changing: 'fas fa-restroom',
        parking: 'fas fa-car',
        showers: 'fas fa-shower',
        equipment: 'fas fa-basketball-ball',
        cafe: 'fas fa-utensils',
        wifi: 'fas fa-wifi',
        first_aid: 'fas fa-first-aid'
      };
      return icons[amenity] || 'fas fa-check';
    },

    formatAmenity(amenity) {
      return amenity.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },

    viewBookingDetails(booking) {
      this.selectedBooking = booking;
    },

    editPitchInfo() {
      this.$router.push({ name: "FacilityOwnerEditFacility" });
    },

    capitalizeTierWord(value) {
      const t = String(value || "").trim();
      if (!t) return "";
      return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
    },

    formatPitchTier(value) {
      return this.capitalizeTierWord(value);
    },

    calculateDuration(startTime, endTime) {
      const start = parseInt(startTime.split(':')[0]);
      const end = parseInt(endTime.split(':')[0]);
      return Math.max(0, end - start);
    },
    
    handleImageError(event) {
      event.target.style.display = 'none';
      event.target.parentElement.innerHTML = '<i class="fas fa-image"></i>';
    },
    
    openImageGallery() {
      // Implement image gallery modal
      this.showToast('Info', 'Image gallery feature coming soon!', 'info');
    }
  }
};
</script>

<style scoped>
/* === SHELL (aligned with SellerDashboard) === */
.dashboard {
  background: var(--dashboard-bg);
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  box-sizing: border-box;
}

.dashboard-content {
  padding: 110px 20px 80px;
  padding-left: max(20px, env(safe-area-inset-left));
  padding-right: max(20px, env(safe-area-inset-right));
  padding-bottom: max(80px, env(safe-area-inset-bottom));
  max-width: 1400px;
  margin: 0 auto;
  color: var(--text-color1);
  box-sizing: border-box;
}

.edit-pitch-footer-host {
  margin: 0;
  padding: 0;
  width: 100%;
  flex-shrink: 0;
}

.edit-pitch-footer-host :deep(.thirdparty-footer) {
  margin-top: 0;
}

.dashboard-error-state {
  background: var(--card-bg);
  border-radius: 24px;
  padding: 48px 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  margin-bottom: 24px;
}

.dashboard-error-state .error-content {
  max-width: 440px;
  margin: 0 auto;
}

.dashboard-error-state .error-icon {
  font-size: 3rem;
  color: var(--pending-color);
  margin-bottom: 16px;
}

.dashboard-error-state h3 {
  margin: 0 0 10px 0;
  font-size: 1.35rem;
  color: var(--heading-color);
}

.dashboard-error-state p {
  margin: 0 0 20px 0;
  color: var(--subtext-color);
  font-size: 0.95rem;
}

.dashboard-error-state .retry-btn {
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: var(--border-color-hover);
  color: var(--text-color3);
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.dashboard-error-state .retry-btn:hover {
  opacity: 0.92;
  transform: translateY(-1px);
}

.pitch-dashboard-main {
  display: grid;
  grid-template-columns: minmax(280px, 360px) 1fr;
  gap: 28px;
  margin-bottom: 32px;
}

/* Profile header (SellerDashboard pattern) */
.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  background: var(--card-bg);
  padding: 28px;
  border-radius: 24px;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
}

.profile-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--gradient-border);
  border-radius: 24px 24px 0 0;
}

.profile-info {
  display: flex;
  align-items: center;
  gap: 22px;
  flex: 1;
  min-width: 0;
}

.pitch-avatar-wrap {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

.profile-avatar.pitch-avatar-wrap {
  position: relative;
}

.profile-img {
  width: 100%;
  height: 100%;
  border-radius: 20px;
  object-fit: cover;
  border: 3px solid var(--profile-border);
  box-shadow: 0 8px 20px var(--profile-shadow);
  transition: all 0.3s ease;
}

.profile-img:hover {
  transform: scale(1.05);
  box-shadow: 0 12px 30px var(--profile-shadow);
}

.pitch-avatar-fallback {
  width: 100%;
  height: 100%;
  border-radius: 20px;
  background: rgba(59, 130, 246, 0.15);
  border: 3px solid var(--profile-border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: var(--border-color-hover);
}

.profile-details {
  flex: 1;
  min-width: 0;
}

.seller-name {
  font-size: clamp(1.2rem, 3.8vw, 1.65rem);
  font-weight: 800;
  margin: 0 0 10px 0;
  background: var(--name-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.profile-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}

.seller-role,
.seller-number {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.88rem;
  color: var(--subtext-color);
  padding: 4px 12px;
  background: var(--meta-bg);
  border-radius: 8px;
}

.last-booking-hint {
  margin: 10px 0 0 0;
  font-size: 0.85rem;
  color: var(--subtext-color);
  display: flex;
  align-items: center;
  gap: 8px;
}

.last-booking-hint i {
  color: var(--border-color-hover);
}

.verified-note {
  margin-left: 8px;
  font-size: 0.8rem;
  color: var(--approved-color);
}

.profile-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dashboard-btn {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.dashboard-btn:hover {
  background: rgba(59, 130, 246, 0.2);
  transform: rotate(45deg);
}

.profile-actions .edit-btn {
  padding: 10px 18px;
  background: var(--border-color-hover);
  border: none;
  border-radius: 12px;
  font-size: 0.92rem;
  cursor: pointer;
  color: var(--text-color3, #0a0a0a);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 18px rgba(0, 255, 255, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.profile-actions .edit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  filter: brightness(1.05);
  box-shadow: 0 6px 22px rgba(0, 255, 255, 0.35);
}

.approval-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.88rem;
  margin-top: 6px;
}

.status-approved {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.status-pending {
  background: var(--pending-bg);
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}

.status-rejected {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.status-unknown {
  background: var(--unknown-bg, rgba(255, 255, 255, 0.06));
  color: var(--unknown-color, var(--subtext-color));
  border: 1px solid rgba(255, 255, 255, 0.12);
}

/* Stats cards — SellerDashboard style */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
  gap: 22px;
  margin-bottom: 32px;
}

.stats-card {
  background: var(--card-bg);
  padding: 22px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
}

.stats-card:hover {
  border-color: var(--card-hover-border);
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
}

.stats-card-inner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.stats-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stats-icon i {
  font-size: 1.35rem;
}

.stats-content {
  min-width: 0;
}

.stats-value {
  margin: 0 0 4px 0;
  font-size: 1.55rem;
  font-weight: 800;
  color: var(--heading-color);
  line-height: 1.15;
}

.stats-label {
  margin: 0;
  font-size: 0.92rem;
  color: var(--subtext-color);
  font-weight: 500;
}

.stats-sublabel {
  margin: 6px 0 0 0;
  font-size: 0.78rem;
  color: var(--subtext-color);
  opacity: 0.85;
}

/* === LOADING OVERLAY === */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--light-overlay);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.loading-content {
  background: var(--light-card);
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.spinner-ring {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}

.spinner-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 64px;
  height: 64px;
  margin: 8px;
  border: 8px solid var(--loader-border);
  border-radius: 50%;
  animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: var(--loader-color) transparent transparent transparent;
}

.loading-content p {
  margin-top: 20px;
  color: var(--subtext-color);
  font-weight: 500;
}

/* === HEADER WITH ANIMATION === */
.dashboard-header {
  text-align: center;
  margin-bottom: 60px;
  color: var(--text-color1);
  position: relative;
  padding: 40px 0;
}

.header-title {
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 15px;
  background: var(--name-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: titleGlow 2s ease-in-out infinite alternate;
}

.header-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 30px;
  color: var(--subtext-color);
}

.header-waves {
  position: absolute;
  bottom: -20px;
  left: 0;
  right: 0;
  height: 40px;
  overflow: hidden;
}

.wave {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 200%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  animation: waveFlow 3s linear infinite;
}

/* === CONFIRMATION MODAL === */
.confirmation-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--light-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(10px);
  padding: max(12px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right))
    max(12px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left));
  box-sizing: border-box;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.confirmation-content {
  background: var(--light-card);
  border-radius: 20px;
  max-width: min(400px, calc(100vw - 24px));
  width: 100%;
  overflow: hidden;
  animation: modalScale 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.confirmation-header {
  padding: 30px 30px 20px;
  text-align: center;
}

.confirmation-header i {
  font-size: 3rem;
  margin-bottom: 15px;
  display: block;
  color: var(--section-icon);
}

.confirmation-header h3 {
  margin: 0;
  color: var(--heading-color);
}

.confirmation-body {
  padding: 0 30px 30px;
  text-align: center;
}

.confirmation-body p {
  color: var(--subtext-color);
  line-height: 1.6;
  margin-bottom: 15px;
  white-space: pre-line;
}

.booking-info {
  background: var(--meta-bg);
  padding: 10px;
  border-radius: 8px;
  margin-top: 15px;
  color: var(--subtext-color);
}

.confirmation-actions {
  display: flex;
  gap: 10px;
  padding: 20px 30px;
  background: rgba(0, 0, 0, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.confirmation-actions button {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-confirm {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.btn-cancel {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.btn-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(72, 187, 120, 0.3);
}

.btn-cancel:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(245, 101, 101, 0.3);
}

/* === PITCH INFO CARD === */
.pitch-info-card {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  height: fit-content;
  position: sticky;
  top: 100px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
}

.pitch-info-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--gradient-border);
  border-radius: 20px 20px 0 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title i {
  font-size: 1.5rem;
  color: var(--section-icon);
}

.card-title h2 {
  margin: 0;
  color: var(--heading-color);
  font-size: 1.5rem;
}

.edit-btn {
  padding: 10px 20px;
  background: var(--border-color-hover);
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  cursor: pointer;
  color: var(--text-color3, #0a0a0a);
  font-weight: 600;
  transition: transform 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 18px rgba(0, 255, 255, 0.25);
}

.edit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  filter: brightness(1.05);
  box-shadow: 0 6px 22px rgba(0, 255, 255, 0.35);
}

.edit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pitch-details {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.detail-row:hover {
  border-color: var(--section-icon);
}

.detail-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--subtext-color);
  font-weight: 500;
}

.detail-label i {
  width: 16px;
  color: var(--section-icon);
}

.detail-value {
  color: var(--heading-color);
  font-weight: 600;
  text-align: right;
  max-width: 200px;
  word-break: break-word;
}

.amenities-grid {
  display: grid;
  grid-template-columns: repeat(3, 32px);
  gap: 6px;
  justify-content: end;
}

.amenity-item {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--meta-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  color: var(--subtext-color);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: help;
  transition: all 0.3s ease;
}

.amenity-item:hover {
  background: var(--section-icon);
  color: var(--button-color);
  transform: scale(1.1);
}

.amenity-tooltip {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--card-bg);
  color: var(--heading-color);
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.75rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.amenity-item:hover .amenity-tooltip {
  opacity: 1;
  visibility: visible;
  top: -35px;
}

/* Availability Toggle */
.availability-toggle {
  position: relative;
  display: inline-block;
  width: 120px;
  height: 32px;
}

.availability-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--rejected-border);
  border-radius: 34px;
  transition: .4s;
  display: flex;
  align-items: center;
  padding: 0 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.slider:before {
  position: absolute;
  content: "";
  height: 24px;
  width: 24px;
  left: 4px;
  bottom: 4px;
  background-color: var(--button-color);
  border-radius: 50%;
  transition: .4s;
}

input:checked + .slider {
  background-color: var(--approved-border);
}

input:checked + .slider:before {
  transform: translateX(48px);
}

.slider-text {
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  position: absolute;
  left: 35px;
  transition: .4s;
  white-space: nowrap;
}

input:checked + .slider .slider-text {
  left: 10px;
}

/* Status Badges */
.status-badge {
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.pending { 
  background: var(--pending-bg); 
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}
.status-badge.confirmed { 
  background: var(--approved-bg); 
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}
.status-badge.completed { 
  background: var(--action-success-bg); 
  color: var(--action-success-color);
  border: 1px solid var(--action-success-color);
}
.status-badge.cancelled { 
  background: var(--rejected-bg); 
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}
.status-badge.active { 
  background: var(--approved-bg); 
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.status-badge.suspended,
.status-badge.terminated {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

/* Pitch Images */
.pitch-images {
  margin-top: 20px;
}

.image-preview {
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.image-preview:hover img {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: var(--button-color);
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* === BOOKINGS SECTION === */
.bookings-section {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  flex-wrap: wrap;
  gap: 20px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-title i {
  font-size: 1.5rem;
  color: var(--section-icon);
}

.section-title h2 {
  margin: 0;
  color: var(--heading-color);
  font-size: 1.5rem;
}

.section-controls {
  display: flex;
  gap: 15px;
}

.date-filter {
  position: relative;
}

.date-filter input {
  padding: 10px 40px 10px 12px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 0.9rem;
  width: 150px;
  background: rgba(255, 255, 255, 0.05);
  color: var(--heading-color);
}

.date-filter i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--subtext-color);
  pointer-events: none;
}

/* Filter Tabs */
.filter-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 25px;
  flex-wrap: wrap;
}

.tab-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--light-overlay);
  color: var(--subtext-color);
  padding: 10px 20px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--heading-color);
}

.tab-btn.active {
  background: rgba(255, 255, 255, 0.05);
  color: var(--heading-color);
  border: 2px solid var(--border-color-hover);
}

.tab-btn.active:hover {
  background: rgba(255, 255, 255, 0.05);
  color: var(--heading-color);
}

.tab-btn.has-count .tab-count {
  background: rgba(255, 255, 255, 0.2);
  color: var(--button-color);
}

.tab-btn:not(.active).has-count .tab-count {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
}

.tab-count {
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 9999px;
  font-weight: 600;
}

/* Bookings List */
.booking-list-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  gap: 20px;
  margin-bottom: 70px;
}

.booking-list-enter-active,
.booking-list-leave-active {
  transition: all 0.4s ease;
}

.booking-list-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

.booking-list-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(-20px);
}

.booking-list-move {
  transition: transform 0.4s ease;
}

.booking-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 20px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.booking-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border-color: var(--card-hover-border);
}

.booking-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: currentColor;
  opacity: 0.3;
}

.booking-card.pending::before { color: var(--pending-border); }
.booking-card.confirmed::before { color: var(--approved-border); }
.booking-card.completed::before { color: #4299e1; }
.booking-card.cancelled::before { color: var(--rejected-border); }
.booking-card.urgent {
  border-color: rgba(255, 152, 0, 0.4);
  box-shadow: 0 0 0 1px rgba(255, 152, 0, 0.25), 0 10px 28px rgba(0, 0, 0, 0.25);
}

.booking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.booking-id {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: var(--heading-color);
}

.urgent-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: 6px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: rgba(255, 152, 0, 0.18);
  color: #ffb74d;
  border: 1px solid rgba(255, 152, 0, 0.35);
}

.booking-meta {
  display: flex;
  align-items: center;
  gap: 15px;
}

.booking-date {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  color: var(--subtext-color);
}

.booking-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.booking-status.pending { 
  background: var(--pending-bg); 
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}
.booking-status.confirmed { 
  background: var(--approved-bg); 
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}
.booking-status.completed { 
  background: var(--action-success-bg); 
  color: var(--action-success-color);
  border: 1px solid var(--action-success-color);
}
.booking-status.cancelled { 
  background: var(--rejected-bg); 
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.booking-body {
  margin-bottom: 15px;
}

.booking-customer {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.customer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--subtext-color);
}

.customer-info h4 {
  margin: 0 0 4px 0;
  font-size: 0.95rem;
  color: var(--heading-color);
}

.customer-contact {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.8rem;
  color: var(--subtext-color);
}

.customer-contact span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.booking-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.time-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.time-slot, .duration {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: var(--heading-color);
}

.booking-amount {
  text-align: right;
}

.amount-label {
  font-size: 0.75rem;
  color: var(--subtext-color);
  margin-bottom: 2px;
}

.amount-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--heading-color);
}

.booking-notes {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 15px;
}

.booking-notes i {
  color: var(--subtext-color);
  margin-top: 2px;
}

.booking-notes p {
  margin: 0;
  font-size: 0.85rem;
  color: var(--subtext-color);
  line-height: 1.4;
}

.booking-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-action {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s ease;
  min-width: 100px;
}

.btn-action:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-success {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.btn-danger {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.btn-primary {
  background: var(--action-primary-bg);
  color: var(--action-primary-color);
  border: 1px solid var(--action-primary-color);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  border: 1px solid var(--border-color-hover);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
}

/* Empty State */
.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: var(--subtext-color);
}

.empty-state h3 {
  color: var(--heading-color);
  margin: 0 0 10px 0;
}

.empty-state p {
  color: var(--subtext-color);
  margin-bottom: 20px;
}

.btn-refresh {
  background: var(--edit-gradient);
  color: var(--button-color);
  border: none;
  padding: 10px 20px;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-refresh:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px var(--edit-shadow);
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.pagination-btn {
  width: 40px;
  height: 40px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--subtext-color);
}

.pagination-btn:hover:not(:disabled) {
  border-color: var(--section-icon);
  color: var(--heading-color);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 5px;
}

.page-btn {
  width: 40px;
  height: 40px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  color: var(--subtext-color);
}

.page-btn:hover:not(.active) {
  border-color: var(--section-icon);
  color: var(--heading-color);
}

.page-btn.active {
  background: var(--section-icon);
  color: var(--button-color);
  border-color: var(--section-icon);
}

/* === BOOKING DETAILS MODAL === */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--light-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: max(12px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right))
    max(12px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left));
  backdrop-filter: blur(10px);
  box-sizing: border-box;
}

.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: all 0.3s ease;
}

.modal-slide-enter-from,
.modal-slide-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.modal-content {
  background: var(--light-card);
  border-radius: 20px;
  max-width: min(700px, 100%);
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalScale 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header {
  padding: 25px 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-title i {
  font-size: 1.5rem;
  color: var(--section-icon);
}

.modal-title h2 {
  margin: 0;
  color: var(--heading-color);
  font-size: 1.5rem;
}

.close-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
  transform: rotate(45deg);
}

.modal-body {
  padding: 30px;
}

.urgent-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(255, 152, 0, 0.4);
  background: rgba(255, 152, 0, 0.12);
  color: #ffcc80;
}

.urgent-alert i {
  margin-top: 2px;
}

.urgent-alert p {
  margin: 4px 0 0;
  font-size: 0.86rem;
  color: var(--subtext-color);
}

.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 25px;
}

.status-indicator.pending { 
  background: var(--pending-bg); 
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}
.status-indicator.confirmed { 
  background: var(--approved-bg); 
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}
.status-indicator.completed { 
  background: var(--action-success-bg); 
  color: var(--action-success-color);
  border: 1px solid var(--action-success-color);
}
.status-indicator.cancelled { 
  background: var(--rejected-bg); 
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  animation: pulseDot 2s infinite;
}

@keyframes pulseDot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.customer-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 25px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.customer-header {
  display: flex;
  align-items: center;
  gap: 15px;
}

.customer-avatar-large {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: var(--section-icon);
}

.customer-info h3 {
  margin: 0 0 8px 0;
  color: var(--heading-color);
}

.customer-contact {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.customer-contact a {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--subtext-color);
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.customer-contact a:hover {
  color: var(--section-icon);
}

.booking-timeline {
  margin-bottom: 25px;
}

.timeline-item {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.timeline-item:last-child {
  border-bottom: none;
}

.timeline-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--section-icon);
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.timeline-content {
  flex: 1;
}

.timeline-content strong {
  display: block;
  color: var(--heading-color);
  margin-bottom: 4px;
}

.timeline-content p {
  margin: 0;
  color: var(--subtext-color);
}

.timeline-content small {
  color: var(--subtext-color);
  font-size: 0.85rem;
}

.timeline-content .price {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--heading-color);
}

.notes-section {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 25px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.notes-section h4 {
  margin: 0 0 10px 0;
  color: var(--heading-color);
  display: flex;
  align-items: center;
  gap: 8px;
}

.notes-content {
  color: var(--subtext-color);
  line-height: 1.6;
}

.pitch-details-section {
  margin-bottom: 25px;
}

.pitch-details-section h4 {
  margin: 0 0 15px 0;
  color: var(--heading-color);
  display: flex;
  align-items: center;
  gap: 8px;
}

.pitch-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 160px), 1fr));
  gap: 15px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.info-item:last-child {
  border-bottom: none;
}

.info-item .label {
  color: var(--subtext-color);
}

.info-item .value {
  color: var(--heading-color);
  font-weight: 600;
}

.timeline-events {
  margin-top: 25px;
}

.timeline-events h4 {
  margin: 0 0 15px 0;
  color: var(--heading-color);
  display: flex;
  align-items: center;
  gap: 8px;
}

.events-list {
  position: relative;
  padding-left: 30px;
}

.events-list::before {
  content: '';
  position: absolute;
  left: 10px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(255, 255, 255, 0.1);
}

.event-item {
  position: relative;
  margin-bottom: 15px;
}

.event-dot {
  position: absolute;
  left: -20px;
  top: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--section-icon);
  border: 2px solid var(--light-card);
  box-shadow: 0 0 0 3px rgba(58, 134, 255, 0.2);
}

.event-content {
  padding-left: 10px;
}

.event-content strong {
  display: block;
  color: var(--heading-color);
  margin-bottom: 4px;
}

.event-content p {
  margin: 0;
  color: var(--subtext-color);
  font-size: 0.9rem;
}

.modal-footer {
  padding: 20px 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.modal-footer .btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 10px 20px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.modal-footer .btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
}

.modal-footer .btn-success {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
  padding: 10px 20px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.modal-footer .btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(72, 187, 120, 0.3);
}

/* === ANIMATIONS === */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInRight {
  from { opacity: 0; transform: translateX(100%); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 0.2; }
  100% { transform: scale(1); opacity: 0.5; }
}

@keyframes titleGlow {
  from { text-shadow: 0 0 20px rgba(255, 255, 255, 0.5); }
  to { text-shadow: 0 0 30px rgba(255, 255, 255, 0.8); }
}

@keyframes waveFlow {
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0); }
}

@keyframes modalScale {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

/* === RESPONSIVE DESIGN === */
@media (max-width: 1200px) {
  .pitch-dashboard-main {
    grid-template-columns: 1fr;
  }

  .pitch-info-card {
    position: static;
  }

  .booking-list-container {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  }

  .bookings-section {
    padding: 24px 20px;
  }
}

@media (max-width: 1024px) {
  .pitch-dashboard-main {
    gap: 22px;
  }

  .profile-header {
    padding: 22px 20px;
  }

  .stats-grid {
    gap: 16px;
  }

  .booking-list-container {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr));
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    padding-top: max(88px, env(safe-area-inset-top));
    padding-bottom: max(64px, env(safe-area-inset-bottom));
    padding-left: max(16px, env(safe-area-inset-left));
    padding-right: max(16px, env(safe-area-inset-right));
  }

  .profile-header {
    flex-direction: column;
    align-items: stretch;
    padding: 20px 16px;
  }

  .profile-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .pitch-avatar-wrap {
    width: 88px;
    height: 88px;
  }

  .profile-actions {
    justify-content: stretch;
    flex-wrap: wrap;
    gap: 12px;
  }

  .profile-actions .edit-btn {
    flex: 1;
    min-width: 0;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .section-header {
    flex-direction: column;
    align-items: stretch;
  }

  .section-controls {
    flex-direction: column;
  }

  .date-filter {
    width: 100%;
  }

  .date-filter input {
    width: 100%;
  }

  .filter-tabs {
    overflow-x: auto;
    padding-bottom: 10px;
    justify-content: flex-start;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }

  .tab-btn {
    white-space: nowrap;
  }

  .booking-list-container {
    grid-template-columns: 1fr;
    margin-bottom: 48px;
  }

  .bookings-section {
    padding: 20px 16px;
  }

  .card-header {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }

  .pitch-info-card .edit-btn {
    width: 100%;
    justify-content: center;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .detail-value {
    max-width: 100%;
    text-align: left;
  }

  .amenities-grid {
    justify-content: flex-start;
  }

  .booking-header {
    flex-wrap: wrap;
    gap: 10px;
  }

  .booking-meta {
    flex-wrap: wrap;
  }

  .booking-actions {
    flex-direction: column;
  }

  .btn-action {
    width: 100%;
    min-width: 0;
    min-height: 44px;
  }

  .modal-content {
    max-height: 92vh;
    width: 100%;
  }

  .modal-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
    padding: 20px 16px;
  }

  .modal-title h2 {
    font-size: clamp(1.1rem, 4vw, 1.5rem);
    word-break: break-word;
  }

  .modal-body {
    padding: 20px 16px;
  }

  .modal-footer {
    flex-direction: column;
    padding: 16px;
    gap: 10px;
  }

  .modal-footer .btn-secondary,
  .modal-footer .btn-success {
    width: 100%;
    justify-content: center;
    min-height: 44px;
  }

  .confirmation-actions {
    flex-direction: column;
    padding: 16px;
  }

  .confirmation-actions .btn-cancel,
  .confirmation-actions .btn-confirm {
    width: 100%;
    min-height: 44px;
  }

  .confirmation-header {
    padding: 24px 20px 16px;
  }

  .confirmation-body {
    padding: 0 20px 24px;
  }

  .pitch-info-grid {
    grid-template-columns: 1fr;
  }

  .pagination {
    flex-wrap: wrap;
    gap: 8px;
    padding-left: 8px;
    padding-right: 8px;
  }

  .page-numbers {
    flex-wrap: wrap;
    justify-content: center;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 4px;
  }

  .customer-contact a {
    word-break: break-word;
  }

  .timeline-item {
    flex-wrap: wrap;
  }
}

@media (max-width: 480px) {
  .dashboard-content {
    padding-top: 76px;
    padding-bottom: max(56px, env(safe-area-inset-bottom));
    padding-left: max(12px, env(safe-area-inset-left));
    padding-right: max(12px, env(safe-area-inset-right));
  }

  .profile-header {
    padding: 16px 12px;
    border-radius: 16px;
  }

  .stats-card {
    padding: 18px 16px;
  }

  .booking-card {
    padding: 15px;
  }

  .booking-details {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .booking-amount {
    text-align: left;
  }

  .bookings-section {
    padding: 16px 12px;
    border-radius: 16px;
  }

  .pitch-info-card {
    padding: 18px 14px;
    border-radius: 16px;
  }

  .card-title h2,
  .section-title h2 {
    font-size: clamp(1.15rem, 5vw, 1.5rem);
  }

  .toast {
    padding: 14px 12px;
  }

  .toast-content strong {
    font-size: 0.9rem;
  }

  .pagination-btn,
  .page-btn {
    min-width: 40px;
  }

  .empty-state {
    padding: 40px 16px;
  }

  .loading-content {
    padding: 28px 20px;
    margin: 0 8px;
  }
}

@media (max-width: 380px) {
  .tab-btn {
    padding: 8px 12px;
    font-size: 0.85rem;
  }

  .booking-list-container {
    gap: 12px;
  }
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    animation-delay: 0ms !important;
    transition-delay: 0ms !important;
  }
  
  .booking-card,
  .stats-card,
  .tab-btn,
  .btn-action,
  .edit-btn {
    transition: none !important;
  }
  
  .booking-card:hover,
  .stats-card:hover {
    transform: none !important;
  }
}
</style>